import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { STATUS_LEGEND, datePart, describeDay, formatHours, statusOf } from './attendanceStatus';

// The month at a glance: a row per person, a letter per day. Drawn the same way on the attendance
// board and in the Attendance Register report, so the two can never drift apart.
//
// `onOpenDay` is optional: the board opens the day's entry, the report has nothing to open.

export const monthKey = (date) => String(date).slice(0, 7);
export const shiftMonth = (ym, by) => {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + by, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};
export const monthLabel = (ym) => datePart(`${ym}-01`, { month: 'long', year: 'numeric' }) || ym;

export const MonthPicker = ({ month, onMonth, max, label = 'month' }) => (
  <div className="flex items-center gap-2">
    <button type="button" aria-label={`Previous ${label}`} onClick={() => onMonth(shiftMonth(month, -1))} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50">
      <ChevronLeft className="w-4 h-4" />
    </button>
    <span className="font-semibold text-gray-900 min-w-[10rem] text-center">{monthLabel(month)}</span>
    <button
      type="button"
      aria-label={`Next ${label}`}
      onClick={() => onMonth(shiftMonth(month, 1))}
      disabled={Boolean(max) && month >= max}
      className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40"
    >
      <ChevronRight className="w-4 h-4" />
    </button>
  </div>
);

const RegisterGrid = ({ data, onOpenDay }) => (
  <div className="bg-white rounded-2xl shadow-xl overflow-x-auto">
    <table className="text-xs border-collapse">
      <thead>
        <tr>
          <th className="sticky left-0 bg-white z-10 px-3 py-2 text-left font-semibold text-gray-500 uppercase tracking-wider min-w-[11rem]">Name</th>
          {data.days.map((d) => (
            <th
              key={d.date}
              title={d.holiday || undefined}
              className={`px-1 py-2 text-center font-medium ${d.holiday ? 'text-teal-700' : d.workingDay ? 'text-gray-600' : 'text-gray-300'} min-w-[1.9rem]`}
            >
              <div>{datePart(d.date, { day: 'numeric' })}</div>
              <div className="text-[10px] font-normal">{datePart(d.date, { weekday: 'narrow' })}</div>
            </th>
          ))}
          <th className="px-2 py-2 text-right font-semibold text-gray-500 uppercase tracking-wider">P</th>
          <th className="px-2 py-2 text-right font-semibold text-gray-500 uppercase tracking-wider">H</th>
          <th className="px-2 py-2 text-right font-semibold text-gray-500 uppercase tracking-wider">A</th>
          <th className="px-2 py-2 text-right font-semibold text-gray-500 uppercase tracking-wider">L</th>
          <th className="px-2 py-2 text-right font-semibold text-gray-500 uppercase tracking-wider">Late</th>
          <th className="px-2 py-2 text-right font-semibold text-gray-500 uppercase tracking-wider">Hours</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {data.rows.length === 0 && (
          <tr><td colSpan={data.days.length + 7} className="px-4 py-8 text-center text-gray-500">No staff.</td></tr>
        )}
        {data.rows.map((r) => (
          <tr key={r.userId}>
            <td className="sticky left-0 bg-white z-10 px-3 py-1.5 whitespace-nowrap">
              <div className="font-medium text-gray-900">{r.userName}</div>
              <div className="text-[10px] text-gray-500">{r.role}</div>
            </td>
            {data.days.map((d) => {
              const day = r.days[d.date];
              const st = statusOf(day);
              const attention = day?.flagged || day?.autoClosed || day?.rejected;
              const clickable = Boolean(onOpenDay && day?.entryId);
              const what = `${r.userName}, ${datePart(d.date, { day: 'numeric', month: 'short' })}: ${describeDay(day) || 'no record'}`;
              return (
                <td key={d.date} className="px-0.5 py-1 text-center">
                  <button
                    type="button"
                    disabled={!clickable}
                    onClick={clickable ? () => onOpenDay(r.userId, d.date) : undefined}
                    title={what}
                    aria-label={what}
                    className={`relative w-7 h-7 rounded-md font-semibold ${st.cell} ${clickable ? 'hover:ring-2 hover:ring-navy-800' : 'cursor-default'}`}
                  >
                    {st.short}
                    {day?.late && <span className="absolute bottom-0.5 right-0.5 text-[8px] leading-none text-amber-700">L</span>}
                    {attention && <span className="absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-red-500" aria-hidden="true" />}
                  </button>
                </td>
              );
            })}
            <td className="px-2 py-1.5 text-right text-gray-700">{r.totals.present}</td>
            <td className="px-2 py-1.5 text-right text-gray-700">{r.totals.halfDays}</td>
            <td className="px-2 py-1.5 text-right text-gray-700">{r.totals.absent}</td>
            <td className="px-2 py-1.5 text-right text-gray-700">{r.totals.leaveDays ?? 0}</td>
            <td className="px-2 py-1.5 text-right text-gray-700">{r.totals.late}</td>
            <td className="px-2 py-1.5 text-right text-gray-700 whitespace-nowrap">{formatHours(r.totals.hours)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const REGISTER_LEGEND = STATUS_LEGEND;

export default RegisterGrid;
